import RequestAbstract from '../abstract';

export default class MarketingCampaigns extends RequestAbstract {
    data;
    constructor(data) {
        super(data);
        this.data = data;
        this.link = 'api/marketing-campaigns';
    }

    // Override method to use GET request
    async getAllFiltered(
        filter = [],
        sort = [{ field: 'id', direction: 'DESC' }],
        props = {}
    ) {
        return await this.getFiltered(
            this.link,
            filter,
            sort,
            {
                page: props.paging?.page || 0,
                pageSize: props.paging?.pageSize || 25
            },
            props
        );
    }

    // Override search method to use GET
    async search(limit, filter = [], sort = [], props) {
        return await this.getFiltered(
            this.link,
            filter,
            sort,
            {
                page: 0,
                pageSize: limit || 25
            },
            props
        );
    }

    // Override getFiltered to use GET request
    async getFiltered(link, filter, sort, paging, props = {}) {
        let url = link;

        // Add parameters to URL
        const params = new URLSearchParams();

        if (paging) {
            if (paging.page !== undefined) {
                params.append('page', paging.page);
            }
            if (paging.pageSize !== undefined) {
                params.append('pageSize', paging.pageSize);
            }
        }

        if (sort && sort.length > 0) {
            params.append('sortField', sort[0].field);
            params.append('sortDirection', sort[0].direction);
        }

        if (filter && filter.length > 0) {
            params.append('filter', JSON.stringify(filter));
        }

        // Add extra properties if present
        if (props && Object.keys(props).length > 0) {
            Object.entries(props).forEach(([key, value]) => {
                if (key !== 'paging' && value !== undefined) {
                    params.append(
                        key,
                        typeof value === 'object'
                            ? JSON.stringify(value)
                            : value
                    );
                }
            });
        }

        if (params.toString()) {
            url += '?' + params.toString();
        }

        return await this.get(url);
    }

    // Override add method for creating new campaigns
    async add(info) {
        const response = await this.loadWithAuth(`${this.link}/add`, {
            method: 'POST',
            body: JSON.stringify(info),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response;
    }

    // Override update method to use PUT request
    async update(id, info) {
        const response = await this.loadWithAuth(`${this.link}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(info),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response;
    }

    // Method to get a single campaign by ID
    async get(url) {
        return await this.loadWithAuth(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    // Method to get campaign by ID (convenience method)
    async getById(id) {
        return await this.get(`${this.link}/${id}`);
    }

    // Method to upload campaign images
    async uploadImage(formData) {
        const url = `${this.link}/upload-image`;
        return await this.loadWithAuth(
            url,
            {
                method: 'POST',
                body: formData
            },
            { fileUpload: true }
        );
    }

    // Method to send campaign (similar to handleSend in editor)
    async send(id) {
        // First, get current campaign data
        const campaignData = await this.getById(id);

        if (campaignData.error !== 'OK') {
            return campaignData;
        }

        // Study audienceLists structure in detail

        // Correctly extract audience IDs
        let audienceIds = [];
        if (
            campaignData.data.audienceLists &&
            Array.isArray(campaignData.data.audienceLists)
        ) {
            audienceIds = campaignData.data.audienceLists
                .map((audience) => {
                    // If it's an object with id field, extract id
                    if (typeof audience === 'object' && audience.id) {
                        return audience.id;
                    }
                    // If it's already a number, return as is
                    if (typeof audience === 'number') {
                        return audience;
                    }
                    // Try to convert to number
                    return parseInt(audience);
                })
                .filter((audienceId) => !isNaN(audienceId));
        }

        // Send full payload as in editor
        let preparedData = {
            campaignName: campaignData.data.campaignName,
            imageUrl: campaignData.data.imageUrl,
            audienceListIds: audienceIds, // Use extracted IDs
            audienceListsNames: campaignData.data.audienceListsNames,
            audienceListUserNumber: campaignData.data.audienceListUserNumber,
            deliveryRate: campaignData.data.deliveryRate,
            clickRate: campaignData.data.clickRate,
            languages: campaignData.data.languages,
            title: campaignData.data.title,
            text: campaignData.data.text,
            locationId: campaignData.data.locationId,
            displayEndDate: campaignData.data.displayEndDate,
            offerEndDate: campaignData.data.campaignEndDate,
            scheduledDate: campaignData.data.scheduledDate,
            notificationTiming: campaignData.data.notificationTiming,
            deepLink: campaignData.data.deepLink,
            status: 'SENT'
        };

        const response = await this.loadWithAuth(`${this.link}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(preparedData),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response;
    }
}
